/**
 * State Badge Components
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.3.1 - View All Projects (lifecycle and control state display)
 *
 * Reusable badge components for displaying project lifecycle and control states.
 */

import { Chip, ChipProps } from '@mui/material';

/**
 * Project lifecycle states from the backend
 * @implements FS-002 - Project Management
 */
export type LifecycleState = 'ACTIVE' | 'ARCHIVED' | 'PENDING_DELETION';

/**
 * Project control states from the backend
 * @implements FS-002.6.1 - Control State Definitions
 */
export type ControlState = 'NORMAL' | 'DRAFT' | 'PENDING';

/**
 * Props for LifecycleStateBadge component
 */
export interface LifecycleStateBadgeProps {
  /** The lifecycle state to display */
  state: LifecycleState;
  /** Size of the badge */
  size?: 'small' | 'medium';
}

/**
 * Badge component for displaying project lifecycle state
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.3.1 - View All Projects
 *
 * Colors:
 * - Active: green (success)
 * - Archived: gray (default)
 * - PendingDeletion: red (error)
 */
export const LifecycleStateBadge = ({
  state,
  size = 'small',
}: LifecycleStateBadgeProps) => {
  const getChipProps = (): Partial<ChipProps> => {
    switch (state) {
      case 'ACTIVE':
        return {
          label: 'Active',
          color: 'success',
        };
      case 'ARCHIVED':
        return {
          label: 'Archived',
          color: 'default',
        };
      case 'PENDING_DELETION':
        return {
          label: 'Pending Deletion',
          color: 'error',
        };
      default:
        return {
          label: state,
          color: 'default',
        };
    }
  };

  const chipProps = getChipProps();

  return (
    <Chip
      {...chipProps}
      size={size}
      variant="outlined"
      sx={{
        fontWeight: 500,
        borderRadius: '16px',
      }}
    />
  );
};

/**
 * Props for ControlStateBadge component
 */
export interface ControlStateBadgeProps {
  /** The control state to display */
  state: ControlState;
  /** Size of the badge */
  size?: 'small' | 'medium';
}

/**
 * Badge component for displaying project control state
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.6.1 - Control State Definitions
 *
 * Colors:
 * - Normal: default (no special emphasis)
 * - Draft: orange (warning)
 * - Pending: blue (info)
 */
export const ControlStateBadge = ({
  state,
  size = 'small',
}: ControlStateBadgeProps) => {
  const getChipProps = (): Partial<ChipProps> => {
    switch (state) {
      case 'NORMAL':
        return {
          label: 'Normal',
          color: 'default',
        };
      case 'DRAFT':
        return {
          label: 'Draft',
          color: 'warning',
        };
      case 'PENDING':
        return {
          label: 'Pending',
          color: 'info',
        };
      default:
        return {
          label: state,
          color: 'default',
        };
    }
  };

  const chipProps = getChipProps();

  return (
    <Chip
      {...chipProps}
      size={size}
      variant="filled"
      sx={{
        fontWeight: 500,
        borderRadius: '16px',
      }}
    />
  );
};
